import React from 'react';
import { Component } from 'react';
import { HealthcareProvider, UserResponseRecord } from '../interfaces';
import { Theme } from '../Theme';
import { Button } from './Button';
import { PlanCatalogAdmin } from '../saas/PlanCatalogAdmin';
import { BillingAttentionAdmin } from '../saas/BillingAttentionAdmin';

type Props = {
    isRoot: boolean;
    isPlatformOperator: boolean;
    healthcareProviders: Array<HealthcareProvider>;
    userResponseKeys: Array<string>;
    commsSenderEmail: string;
    commsReplyToEmail: string;
    hasResendApiKey: boolean;
    commsCampaignHourlyEnabled: boolean;
    commsCampaignRuns: Array<{
        id: string;
        triggerSource: string;
        ranAt: string;
        smsSent: number;
        emailSent: number;
        pushSent: number;
    }>;
    onDownloadAssessmentAnswers: (filters: { healthcareProviderId?: string; key?: string }) => Promise<Array<UserResponseRecord>>;
    onUpdateCommsEmailSettings: (settings: { senderEmail: string; replyToEmail: string; resendApiKey?: string }) => Promise<void>;
    onSetCommsCampaignHourlyEnabled: (hourlyEnabled: boolean) => Promise<void>;
    onRunCommsCampaigns: () => Promise<{
        scannedClients: number;
        clientsWithCampaign: number;
        dueMessages: number;
        smsSent: number;
        emailSent: number;
        smsSkippedMissingPhone: number;
        emailSkippedMissingEmail: number;
        emailSkippedMissingSender: number;
        emailSkippedMissingApiKey: number;
        pushSkippedUnsupported: number;
        alreadyProcessed: number;
        skippedMissingTriggerTime: number;
        skippedNoMatchingCampaign: number;
        errors: number;
    }>;
};

type State = {
    selectedHealthcareProviderId: string;
    selectedUserResponseKey: string;
    commsSenderEmail: string;
    commsReplyToEmail: string;
    resendApiKey: string;
    isRunningCommsCampaigns: boolean;
    isSavingCommsSenderEmail: boolean;
    isSavingCommsCampaignHourlyEnabled: boolean;
};

export class ToolsView extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selectedHealthcareProviderId: 'All',
            selectedUserResponseKey: 'All',
            commsSenderEmail: props.commsSenderEmail || '',
            commsReplyToEmail: props.commsReplyToEmail || '',
            resendApiKey: '',
            isRunningCommsCampaigns: false,
            isSavingCommsSenderEmail: false,
            isSavingCommsCampaignHourlyEnabled: false,
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (
            prevProps.commsSenderEmail === this.props.commsSenderEmail &&
            prevProps.commsReplyToEmail === this.props.commsReplyToEmail
        ) {
            return;
        }

        if (this.state.commsSenderEmail !== this.props.commsSenderEmail) {
            this.setState({ commsSenderEmail: this.props.commsSenderEmail });
        }

        if (this.state.commsReplyToEmail !== this.props.commsReplyToEmail) {
            this.setState({ commsReplyToEmail: this.props.commsReplyToEmail });
        }
    }

    render() {
        return (
            <div style={styles.container}>
                {this.props.isPlatformOperator && <BillingAttentionAdmin />}
                {this.props.isRoot && <PlanCatalogAdmin />}

                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Download Assessments</div>
                    <div style={styles.controlsRow}>
                        <div style={styles.controlGroup}>
                            <label style={styles.label}>Healthcare provider</label>
                            <select
                                value={this.state.selectedHealthcareProviderId}
                                onChange={(e) => this.setState({ selectedHealthcareProviderId: e.target.value })}
                                style={styles.select}
                            >
                                <option value='All'>All</option>
                                {this.props.healthcareProviders.map((provider) => (
                                    <option key={provider.id} value={provider.id}>
                                        {provider.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={styles.controlGroup}>
                            <label style={styles.label}>Assessment type</label>
                            <select
                                value={this.state.selectedUserResponseKey}
                                onChange={(e) => this.setState({ selectedUserResponseKey: e.target.value })}
                                style={styles.select}
                            >
                                <option value='All'>All</option>
                                {this.props.userResponseKeys.map((key) => (
                                    <option key={key} value={key}>
                                        {key}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <Button
                        onClick={async () => {
                            try {
                                const healthcareProviderId = this.state.selectedHealthcareProviderId === 'All'
                                    ? undefined
                                    : this.state.selectedHealthcareProviderId;
                                const key = this.state.selectedUserResponseKey === 'All'
                                    ? undefined
                                    : this.state.selectedUserResponseKey;

                                const results = await this.props.onDownloadAssessmentAnswers({
                                    healthcareProviderId,
                                    key,
                                });

                                const fileNameParts = [
                                    'assessment-answers',
                                    healthcareProviderId || 'all-providers',
                                    key || 'all-types',
                                ];
                                const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
                                const objectUrl = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = objectUrl;
                                link.download = `${fileNameParts.join('-')}.json`;
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                URL.revokeObjectURL(objectUrl);
                            } catch (error) {
                                console.error('TOOLS_VIEW: Error downloading assessment answers:', error);
                                window.alert(error instanceof Error ? error.message : 'Failed to download assessment answers.');
                            }
                        }}
                        label='Download assessment answers'
                        type='primary'
                        style={styles.actionButton}
                    />
                </div>

                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Comms Campaigns</div>
                    <div style={styles.campaignControlsRow}>
                        <label style={styles.toggleLabel}>
                            <button
                                type='button'
                                onClick={async () => {
                                    try {
                                        this.setState({ isSavingCommsCampaignHourlyEnabled: true });
                                        await this.props.onSetCommsCampaignHourlyEnabled(!this.props.commsCampaignHourlyEnabled);
                                    } catch (error) {
                                        console.error('TOOLS_VIEW: Error updating hourly comms campaign setting:', error);
                                        window.alert(error instanceof Error ? error.message : 'Failed to update hourly comms campaign setting.');
                                    } finally {
                                        this.setState({ isSavingCommsCampaignHourlyEnabled: false });
                                    }
                                }}
                                style={{
                                    ...styles.toggleButton,
                                    backgroundColor: this.props.commsCampaignHourlyEnabled ? '#ef5350' : '#d7d7d7',
                                }}
                                disabled={this.state.isSavingCommsCampaignHourlyEnabled}
                            >
                                <span
                                    style={{
                                        ...styles.toggleKnob,
                                        transform: this.props.commsCampaignHourlyEnabled ? 'translateX(24px)' : 'translateX(0)',
                                    }}
                                />
                            </button>
                            <span>
                                Background hourly run: {this.props.commsCampaignHourlyEnabled ? 'ON' : 'OFF'}
                            </span>
                        </label>
                    </div>

                    <Button
                        onClick={async () => {
                            try {
                                this.setState({ isRunningCommsCampaigns: true });
                                const summary = await this.props.onRunCommsCampaigns();
                                window.alert(
                                    [
                                        'Comms campaigns run complete',
                                        `Scanned clients: ${summary.scannedClients}`,
                                        `Clients with campaign: ${summary.clientsWithCampaign}`,
                                        `Due messages: ${summary.dueMessages}`,
                                        `SMS sent: ${summary.smsSent}`,
                                        `Email sent: ${summary.emailSent}`,
                                        `SMS skipped (missing phone): ${summary.smsSkippedMissingPhone}`,
                                        `Email skipped (missing recipient email): ${summary.emailSkippedMissingEmail}`,
                                        `Email skipped (missing sender email): ${summary.emailSkippedMissingSender}`,
                                        `Email skipped (missing Resend API key): ${summary.emailSkippedMissingApiKey}`,
                                        `Push skipped (unsupported): ${summary.pushSkippedUnsupported}`,
                                        `Already processed: ${summary.alreadyProcessed}`,
                                        `Skipped (missing trigger time): ${summary.skippedMissingTriggerTime}`,
                                        `Skipped (missing campaign): ${summary.skippedNoMatchingCampaign}`,
                                        `Errors: ${summary.errors}`,
                                    ].join('\n')
                                );
                            } catch (error) {
                                console.error('TOOLS_VIEW: Error running comms campaigns:', error);
                                window.alert(error instanceof Error ? error.message : 'Failed to run comms campaigns.');
                            } finally {
                                this.setState({ isRunningCommsCampaigns: false });
                            }
                        }}
                        label={this.state.isRunningCommsCampaigns ? 'Running comms campaigns...' : 'Run comms campaigns'}
                        type='secondary'
                        style={{
                            ...styles.actionButton,
                            backgroundColor: '#ffebee',
                            color: '#d32f2f',
                            border: '1px solid #ef5350',
                        }}
                        disabled={this.state.isRunningCommsCampaigns}
                    />

                    <div style={styles.logSection}>
                        <div style={styles.logTitle}>Run Log</div>
                        {this.props.commsCampaignRuns.length === 0 ? (
                            <div style={styles.helperText}>No comms campaign runs logged yet.</div>
                        ) : (
                            <div style={styles.logTableWrapper}>
                                <table style={styles.logTable}>
                                    <thead>
                                        <tr style={styles.logHeaderRow}>
                                            <th style={styles.logHeaderCell}>Run time</th>
                                            <th style={styles.logHeaderCell}>Source</th>
                                            <th style={styles.logHeaderCell}>SMS sent</th>
                                            <th style={styles.logHeaderCell}>Email sent</th>
                                            <th style={styles.logHeaderCell}>Push sent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.props.commsCampaignRuns.map((run) => (
                                            <tr key={run.id}>
                                                <td style={styles.logCell}>
                                                    {run.ranAt ? new Date(run.ranAt).toLocaleString() : '--'}
                                                </td>
                                                <td style={styles.logCell}>
                                                    {run.triggerSource === 'hourly_schedule' ? 'Hourly' : 'Manual'}
                                                </td>
                                                <td style={styles.logCell}>{run.smsSent}</td>
                                                <td style={styles.logCell}>{run.emailSent}</td>
                                                <td style={styles.logCell}>{run.pushSent}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Comms Email Settings</div>
                    <div style={styles.controlsRow}>
                        <div style={styles.controlGroup}>
                            <label style={styles.label}>Default sender email</label>
                            <input
                                type='email'
                                value={this.state.commsSenderEmail}
                                onChange={(e) => this.setState({ commsSenderEmail: e.target.value })}
                                style={styles.input}
                                placeholder='hello@messages.superpenguin.com'
                                disabled={this.state.isSavingCommsSenderEmail}
                            />
                        </div>

                        <div style={styles.controlGroup}>
                            <label style={styles.label}>Resend API key</label>
                            <input
                                type='password'
                                value={this.state.resendApiKey}
                                onChange={(e) => this.setState({ resendApiKey: e.target.value })}
                                style={styles.input}
                                placeholder={this.props.hasResendApiKey ? 'Configured. Leave blank to keep existing key.' : 're_...'}
                                disabled={this.state.isSavingCommsSenderEmail}
                            />
                        </div>

                        <div style={styles.controlGroup}>
                            <label style={styles.label}>Reply-to email</label>
                            <input
                                type='email'
                                value={this.state.commsReplyToEmail}
                                onChange={(e) => this.setState({ commsReplyToEmail: e.target.value })}
                                style={styles.input}
                                placeholder='support@superpenguin.com'
                                disabled={this.state.isSavingCommsSenderEmail}
                            />
                        </div>
                    </div>

                    <div style={styles.helperText}>
                        Messaging emails use the provider name with an address at messages.superpenguin.com.
                        The default sender is used for account verification and when a provider name is unavailable.
                        Messaging replies go to the Team member selected as the organisation’s reply-to contact.
                        If none is available, replies go to the Reply-to email here, or the default sender when it is blank.
                        The API key is shared by all providers and hidden after saving; leave it blank to keep the existing key.
                    </div>

                    <Button
                        onClick={async () => {
                            try {
                                this.setState({ isSavingCommsSenderEmail: true });
                                await this.props.onUpdateCommsEmailSettings({
                                    senderEmail: this.state.commsSenderEmail,
                                    replyToEmail: this.state.commsReplyToEmail,
                                    resendApiKey: this.state.resendApiKey.trim() || undefined,
                                });
                                this.setState({ resendApiKey: '' });
                                window.alert('Comms email settings saved.');
                            } catch (error) {
                                console.error('TOOLS_VIEW: Error saving comms email settings:', error);
                                window.alert(error instanceof Error ? error.message : 'Failed to save comms email settings.');
                            } finally {
                                this.setState({ isSavingCommsSenderEmail: false });
                            }
                        }}
                        label={this.state.isSavingCommsSenderEmail ? 'Saving email settings...' : 'Save email settings'}
                        type='primary'
                        style={styles.actionButton}
                        disabled={this.state.isSavingCommsSenderEmail}
                    />
                </div>
            </div>
        );
    }
}

const styles = {
    container: {
        width: 'calc(100% - 72px)',
        background: Theme.Colours.background,
        padding: '36px',
        boxSizing: 'border-box' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        flex: 1,
        minHeight: 0,
        gap: '24px',
        overflowY: 'auto' as const,
        overflowX: 'hidden' as const,
    },
    controlsRow: {
        display: 'flex',
        gap: '24px',
        flexWrap: 'wrap' as const,
    },
    controlGroup: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        width: 'min(500px, 100%)',
    },
    label: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        color: Theme.Colours.text,
    },
    select: {
        ...Theme.Fonts.body,
        marginLeft: '0px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
    },
    input: {
        ...Theme.Fonts.body,
        marginLeft: '0px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
    },
    section: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '16px',
        padding: '24px',
        borderRadius: '12px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.workArea,
    },
    sectionTitle: {
        ...Theme.Fonts.title2,
        color: Theme.Colours.text,
        fontWeight: 'bold' as const,
    },
    helperText: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        opacity: 0.8,
    },
    campaignControlsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap' as const,
    },
    toggleLabel: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    toggleButton: {
        marginLeft: '0px',
        width: '56px',
        height: '32px',
        borderRadius: '999px',
        border: 'none',
        padding: '4px',
        cursor: 'pointer',
        position: 'relative' as const,
        transition: 'background-color 0.15s ease',
        flexShrink: 0,
    },
    toggleKnob: {
        display: 'block',
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: '#ffffff',
        transition: 'transform 0.15s ease',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    },
    logSection: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
    },
    logTitle: {
        ...Theme.Fonts.title2,
        color: Theme.Colours.text,
        fontWeight: 'bold' as const,
    },
    logTableWrapper: {
        maxHeight: '420px',
        overflowX: 'auto' as const,
        overflowY: 'auto' as const,
    },
    logTable: {
        width: '100%',
        minWidth: '560px',
        borderCollapse: 'collapse' as const,
        backgroundColor: Theme.Colours.background,
        borderRadius: '8px',
        overflow: 'hidden',
        border: Theme.Colours.border,
    },
    logHeaderRow: {
        backgroundColor: Theme.Colours.tableHeader,
    },
    logHeaderCell: {
        ...Theme.Fonts.body,
        fontWeight: 'bold' as const,
        color: Theme.Colours.text,
        textAlign: 'left' as const,
        padding: '12px 16px',
        borderBottom: Theme.Colours.border,
    },
    logCell: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        padding: '12px 16px',
        borderBottom: Theme.Colours.border,
    },
    actionButton: {
        marginLeft: '0px',
        width: 'min(500px, 100%)',
    },
};
